















// src/services/restaurant/RestaurantInventoryService.js
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { formatCreatedAtDate, formatNumberWithComma } from '../../utils/helpers';

// Helper function to get supplier name
const getSupplierName = (supplierId, supplierNamesData) => {
if (!supplierNamesData || !Array.isArray(supplierNamesData)) return 'Unknown';
const supplier = supplierNamesData.find(s => parseInt(s.id) === parseInt(supplierId));
return supplier ? supplier.name : 'Unknown';
};

// Helper function to get stock name
const getStockName = (stockId, stockNamesData) => {
if (!stockNamesData || !Array.isArray(stockNamesData)) return 'Unknown';
const stock = stockNamesData.find(s => parseInt(s.id) === parseInt(stockId));
return stock ? stock.name : 'Unknown';
};

// Helper function to get branch name
const getBranchName = (branchId, branchNamesData) => {
if (!branchNamesData || !Array.isArray(branchNamesData)) return 'Unknown';
const branch = branchNamesData.find(b => parseInt(b.id) === parseInt(branchId));
return branch ? `${branch.name}` : 'Unknown';
};

// Helper function to format currency
const formatCurrency = (amount) => {
return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
}).format(amount || 0);
};

// Helper function to format date
const formatDate = (dateString) => {
if (!dateString) return 'N/A';
return new Date(dateString).toLocaleDateString();
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
if (!text) return [''];
const words = String(text).split(' ');
const lines = [];
let currentLine = words[0] || '';

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for PDF
const drawInventoryTable = (doc, data, startY) => {
const pageWidth = doc.internal.pageSize.width;
const margin = 14;
const rowHeight = 12;

// Column widths: #, Stock Date, Supplier, Stock Item, Quantity, Cost, Selling, Branch
const colWidths = [15, 25, 35, 45, 20, 25, 25, 30];

// Check if we need a new page
let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;
if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('landscape');
    currentY = margin;
}

// Draw table header
doc.setFillColor(41, 128, 185);
doc.setTextColor(255, 255, 255);
doc.setFont(undefined, 'bold');

const headers = ['#', 'Stock Date', 'Supplier', 'Stock Item', 'Quantity', 'Cost', 'Selling', 'Branch'];
let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

// Draw header underline
doc.setDrawColor(255, 255, 255);
doc.setLineWidth(0.5);
doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0);

data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
    doc.addPage('landscape');
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(41, 128, 185);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    doc.setDrawColor(255, 255, 255);
    doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248);
    } else {
    doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    });
    
    // Add border between rows
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    currentY += actualRowHeight;
});

return currentY;
};

// Excel export for Restaurant Inventory
export const exportToExcel = (items, dataSources = {}, filters = {}) => {
try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = dataSources;
    
    // Prepare data for Excel
    const excelData = items.map((item, index) => {
    const branchName = getBranchName(item.branch_id, branchNamesFilterData);
    const supplierName = getSupplierName(item.supplier_id, supplierNamesData);
    const stockName = getStockName(item.stock_id, stockNamesData);
    
    return {
        'No.': index + 1,
        'Stock Date': formatDate(item.stock_date),
        'Supplier': supplierName,
        'Stock Item': stockName,
        'Quantity': item.quantity || 0,
        'Cost Price': formatCurrency(item.cost),
        'Selling Price': formatCurrency(item.selling),
        'Total Cost': formatCurrency((item.quantity || 0) * (item.cost || 0)),
        'Total Selling': formatCurrency((item.quantity || 0) * (item.selling || 0)),
        'Profit': formatCurrency(((item.selling || 0) - (item.cost || 0)) * (item.quantity || 0)),
        'Branch': branchName,
        'Description': item.description || '',
        'Created Date': formatCreatedAtDate(item.created_at),
        'Updated Date': formatDate(item.updated_at)
    };
    });

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
    Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
        widths[idx] = length;
        }
    });
    return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Restaurant Inventory');

    // Generate Excel file and download
    const fileName = `restaurant_inventory_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Restaurant Inventory Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
}
};

// PDF export for Restaurant Inventory
export const exportToPDF = (items, dataSources = {}, filters = {}) => {
try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = dataSources;
    
    const doc = new jsPDF('landscape');
    
    // Set document properties
    doc.setProperties({
    title: 'Restaurant Inventory Report',
    subject: 'Restaurant Inventory Export',
    author: 'Restaurant Management System',
    keywords: 'restaurant, inventory, stock, export, report',
    creator: 'Restaurant Management System'
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('RESTAURANT INVENTORY REPORT', doc.internal.pageSize.width / 2, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    
    // Report info in two columns
    const leftColumnX = 14;
    const rightColumnX = doc.internal.pageSize.width / 2 + 20;
    
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, leftColumnX, currentY);
    doc.text(`Total Items: ${items.length}`, rightColumnX, currentY);
    currentY += 8;
    
    // Calculate totals
    if (items.length > 0) {
    const totalQuantity = items.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
    const totalCost = items.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.cost) || 0)), 0);
    const totalSelling = items.reduce((sum, item) => sum + ((parseFloat(item.quantity) || 0) * (parseFloat(item.selling) || 0)), 0);
    const totalProfit = totalSelling - totalCost;
    
    doc.text(`Total Quantity: ${formatNumberWithComma(totalQuantity)}`, leftColumnX, currentY);
    doc.text(`Total Cost Value: ${formatCurrency(totalCost)}`, rightColumnX, currentY);
    currentY += 8;
    doc.text(`Total Selling Value: ${formatCurrency(totalSelling)}`, leftColumnX, currentY);
    doc.text(`Total Profit: ${formatCurrency(totalProfit)}`, rightColumnX, currentY);
    currentY += 8;
    }
    
    // Filters applied
    if (filters.supplier_id || filters.dateFrom || filters.dateTo) {
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.text('Applied Filters:', leftColumnX, currentY);
    currentY += 6;
    
    let filterLines = [];
    if (filters.supplier_id) filterLines.push(`• Supplier: ${getSupplierName(filters.supplier_id, supplierNamesData)}`);
    if (filters.dateFrom) filterLines.push(`• From date: ${filters.dateFrom}`);
    if (filters.dateTo) filterLines.push(`• To date: ${filters.dateTo}`);
    
    // Display filters in two columns if many
    const midPoint = Math.ceil(filterLines.length / 2);
    const leftFilters = filterLines.slice(0, midPoint);
    const rightFilters = filterLines.slice(midPoint);
    
    const maxLines = Math.max(leftFilters.length, rightFilters.length);
    
    for (let i = 0; i < maxLines; i++) {
        if (leftFilters[i]) {
        doc.text(leftFilters[i], leftColumnX + 5, currentY);
        }
        if (rightFilters[i]) {
        doc.text(rightFilters[i], rightColumnX, currentY);
        }
        currentY += 6;
    }
    currentY += 6;
    }
    
    // Prepare table data
    const tableData = items.map((item, index) => {
    const branchName = getBranchName(item.branch_id, branchNamesFilterData);
    const supplierName = getSupplierName(item.supplier_id, supplierNamesData);
    const stockName = getStockName(item.stock_id, stockNamesData);
    
    return [
        (index + 1).toString(),
        formatDate(item.stock_date),
        supplierName,
        stockName,
        formatNumberWithComma(item.quantity || 0),
        formatCurrency(item.cost),
        formatCurrency(item.selling),
        branchName
    ];
    });
    
    // Draw the table
    currentY = drawInventoryTable(doc, tableData, currentY + 10);
    
    // Add footer with page number
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
    );
    doc.text(
        `Generated on: ${new Date().toLocaleDateString()}`,
        doc.internal.pageSize.width - 14,
        doc.internal.pageSize.height - 10,
        { align: 'right' }
    );
    }
    
    const fileName = `restaurant_inventory_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
} catch (error) {
    console.error('Restaurant Inventory PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
}
};

// CSV export for Restaurant Inventory
export const exportToCSV = (items, dataSources = {}) => {
try {
    const { supplierNamesData = [], stockNamesData = [], branchNamesFilterData = [] } = dataSources;
    
    // CSV headers
    const headers = [
    'No',
    'Stock Date',
    'Supplier',
    'Stock Item',
    'Quantity',
    'Cost Price',
    'Selling Price',
    'Total Cost',
    'Total Selling',
    'Profit',
    'Branch',
    'Description',
    'Created Date',
    'Updated Date'
    ];
    
    // Prepare CSV data
    const csvData = items.map((item, index) => {
    const branchName = getBranchName(item.branch_id, branchNamesFilterData);
    const supplierName = getSupplierName(item.supplier_id, supplierNamesData);
    const stockName = getStockName(item.stock_id, stockNamesData);
    const totalCost = (item.quantity || 0) * (item.cost || 0);
    const totalSelling = (item.quantity || 0) * (item.selling || 0);
    const profit = totalSelling - totalCost;
    
    return [
        index + 1,
        `"${formatDate(item.stock_date)}"`,
        `"${supplierName.replace(/"/g, '""')}"`,
        `"${stockName.replace(/"/g, '""')}"`,
        item.quantity || 0,
        item.cost || 0,
        item.selling || 0,
        totalCost.toFixed(2),
        totalSelling.toFixed(2),
        profit.toFixed(2),
        `"${branchName.replace(/"/g, '""')}"`,
        `"${(item.description || '').replace(/"/g, '""')}"`,
        `"${formatCreatedAtDate(item.created_at)}"`,
        `"${formatDate(item.updated_at)}"`
    ];
    });

    // Create CSV content
    const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `restaurant_inventory_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
} catch (error) {
    console.error('Restaurant Inventory CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
}
};





